import { create } from 'zustand';
import type { Category, Product } from '../types';
import { db, isFirebaseActive, removeUndefined } from '../firebase';
import { ref as dbRef, set as dbSet, remove as dbRemove, get as dbGet } from 'firebase/database';
import { parsedProducts } from './parsedProducts';

interface ProductState {
  categories: Category[];
  products: Product[];
  imagesMap: Record<string, { imageUrl: string; images: string[] }>;
  isProductsLoaded: boolean;
  isLoadingProducts: boolean;
  isLoadingCategories: boolean;
  currentCategoryId: string | null;  // which category is currently loaded in products[]
  error: string | null;

  // Actions
  fetchProductImage: (productId: string) => Promise<void>;
  addProduct: (product: Omit<Product, 'id' | 'rating' | 'reviewsCount'>) => Promise<void>;
  updateProduct: (id: string, product: Partial<Product>) => Promise<void>;
  deductProductStock: (id: string, quantity: number, variantId?: string) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
  addCategory: (category: Omit<Category, 'subCategories'>) => Promise<void>;
  addSubCategory: (categoryId: string, name: string) => Promise<void>;
  deleteCategory: (categoryId: string) => Promise<void>;
  deleteSubCategory: (categoryId: string, subCategoryId: string) => Promise<void>;
  resetToDefaults: () => Promise<void>;
}

export const defaultCategories: Category[] = [
  {
    id: 'electronic-components',
    name: 'Electronic Components',
    icon: 'Cpu',
    subCategories: [
      { id: 'resistors', name: 'Resistors', categoryId: 'electronic-components' },
      { id: 'capacitors', name: 'Capacitors', categoryId: 'electronic-components' },
      { id: 'inductors', name: 'Inductors', categoryId: 'electronic-components' },
      { id: 'diodes', name: 'Diodes', categoryId: 'electronic-components' },
      { id: 'transistors', name: 'Transistors', categoryId: 'electronic-components' },
      { id: 'ics-chips', name: 'ICs & Chips', categoryId: 'electronic-components' },
      { id: 'voltage-regulators', name: 'Voltage Regulators', categoryId: 'electronic-components' },
      { id: 'switches', name: 'Switches', categoryId: 'electronic-components' },
      { id: 'buzzers', name: 'Buzzers', categoryId: 'electronic-components' },
      { id: 'battery-holders', name: 'Battery Holders', categoryId: 'electronic-components' }
    ]
  },
  {
    id: 'modules-boards',
    name: 'Modules & Boards',
    icon: 'Layers',
    subCategories: [
      { id: 'arduino-boards', name: 'Arduino Boards', categoryId: 'modules-boards' },
      { id: 'esp-modules', name: 'ESP Modules', categoryId: 'modules-boards' },
      { id: 'raspberry-pi-boards', name: 'Raspberry Pi Boards', categoryId: 'modules-boards' },
      { id: 'sensor-modules', name: 'Sensor Modules', categoryId: 'modules-boards' },
      { id: 'communication-modules', name: 'Communication Modules', categoryId: 'modules-boards' },
      { id: 'display-modules', name: 'Display Modules', categoryId: 'modules-boards' },
      { id: 'power-modules', name: 'Power Modules', categoryId: 'modules-boards' },
      { id: 'relay-modules', name: 'Relay Modules', categoryId: 'modules-boards' },
      { id: 'motor-driver-modules', name: 'Motor Driver Modules', categoryId: 'modules-boards' },
      { id: 'audio-modules', name: 'Audio Modules', categoryId: 'modules-boards' },
      { id: 'rtc-memory-modules', name: 'RTC & Memory Modules', categoryId: 'modules-boards' },
      { id: 'rfid-access-modules', name: 'RFID & Access Modules', categoryId: 'modules-boards' },
      { id: 'gps-gsm-modules', name: 'GPS & GSM Modules', categoryId: 'modules-boards' },
      { id: 'iot-modules', name: 'IoT Modules', categoryId: 'modules-boards' },
      { id: 'development-boards', name: 'Development Boards', categoryId: 'modules-boards' }
    ]
  },
  {
    id: 'motors-robotics',
    name: 'Motors & Robotics',
    icon: 'Navigation',
    subCategories: [
      { id: 'dc-motors', name: 'DC Motors', categoryId: 'motors-robotics' },
      { id: 'geared-motors', name: 'Geared Motors', categoryId: 'motors-robotics' },
      { id: 'servo-motors', name: 'Servo Motors', categoryId: 'motors-robotics' },
      { id: 'stepper-motors', name: 'Stepper Motors', categoryId: 'motors-robotics' },
      { id: 'motor-drivers', name: 'Motor Drivers', categoryId: 'motors-robotics' },
      { id: 'wheels-tyres', name: 'Wheels & Tyres', categoryId: 'motors-robotics' },
      { id: 'robotic-chassis', name: 'Robotic Chassis', categoryId: 'motors-robotics' },
      { id: 'robotic-arms', name: 'Robotic Arms', categoryId: 'motors-robotics' },
      { id: 'robotics-kits', name: 'Robotics Kits', categoryId: 'motors-robotics' }
    ]
  },
  {
    id: 'tools-prototyping',
    name: 'Tools & Prototyping',
    icon: 'Wrench',
    subCategories: [
      { id: 'breadboards', name: 'Breadboards', categoryId: 'tools-prototyping' },
      { id: 'veroboards', name: 'Veroboards', categoryId: 'tools-prototyping' },
      { id: 'pcb-boards', name: 'PCB Boards', categoryId: 'tools-prototyping' },
      { id: 'jumper-wires', name: 'Jumper Wires', categoryId: 'tools-prototyping' },
      { id: 'soldering-tools', name: 'Soldering Tools', categoryId: 'tools-prototyping' },
      { id: 'multimeters', name: 'Multimeters', categoryId: 'tools-prototyping' },
      { id: 'power-supplies', name: 'Power Supplies', categoryId: 'tools-prototyping' },
      { id: 'logic-analyzers', name: 'Logic Analyzers', categoryId: 'tools-prototyping' },
      { id: 'prototype-kits', name: 'Prototype Kits', categoryId: 'tools-prototyping' },
      { id: 'diy-kits', name: 'DIY Kits', categoryId: 'tools-prototyping' }
    ]
  }
];

export const defaultProducts: Product[] = parsedProducts;

// Instant initial cache loader for 0ms load speed
function getInitialProducts(): Product[] {
  try {
    const cached = localStorage.getItem('sensorzone_products_cache');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (e) { }
  return []; // Clean empty array — no dummy/fake products or fake images!
}

function getInitialImagesMap(): Record<string, { imageUrl: string; images: string[] }> {
  try {
    const cached = localStorage.getItem('sensorzone_images_cache');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch (e) { }
  return {};
}

const initialProds = getInitialProducts();
const initialImages = getInitialImagesMap();

/** Strip base64 data:// images before writing to RTDB to keep payload small */
function stripBase64Images<T extends { images?: string[] }>(product: T): T {
  if (!product.images) return product;
  return {
    ...product,
    images: product.images.filter((img) => typeof img === 'string' && (img.startsWith('http') || img.startsWith('data:')))
  };
}

const activeProductImageFetches = new Set<string>();

export const useProductStore = create<ProductState>()((set) => ({
  categories: defaultCategories,
  products: initialProds,
  imagesMap: initialImages,
  isProductsLoaded: initialProds.length > 0,
  isLoadingProducts: initialProds.length === 0,
  isLoadingCategories: false,
  currentCategoryId: null,
  error: null,

  fetchProductImage: async (productId) => {
    const state = useProductStore.getState();
    // 1. If already in imagesMap cache, return
    if (state.imagesMap[productId]) return;

    // 2. If currently fetching, return to avoid concurrent duplicate requests
    if (activeProductImageFetches.has(productId)) return;
    activeProductImageFetches.add(productId);

    if (isFirebaseActive && db) {
      try {
        const snap = await dbGet(dbRef(db, `productImages/${productId}`));
        const localProd = state.products.find(p => p.id === productId);
        if (snap.exists()) {
          const data = snap.val();
          set((state) => ({
            imagesMap: {
              ...state.imagesMap,
              [productId]: {
                imageUrl: data.imageUrl || localProd?.imageUrl || '',
                images: (Array.isArray(data.images) ? data.images : Object.values(data.images || {})).filter(Boolean) as string[]
              }
            }
          }));
        } else {
          set((state) => ({
            imagesMap: {
              ...state.imagesMap,
              [productId]: {
                imageUrl: localProd?.imageUrl || '',
                images: localProd?.images || []
              }
            }
          }));
        }
      } catch (err) {
        console.error(`Failed to fetch images for ${productId}:`, err);
        // On error, write fallback data to cache to prevent infinite hook loop retries
        set((state) => ({
          imagesMap: {
            ...state.imagesMap,
            [productId]: {
              imageUrl: '',
              images: []
            }
          }
        }));
      } finally {
        activeProductImageFetches.delete(productId);
      }
    } else {
      activeProductImageFetches.delete(productId);
      const localProd = state.products.find(p => p.id === productId);
      set((state) => ({
        imagesMap: {
          ...state.imagesMap,
          [productId]: {
            imageUrl: localProd?.imageUrl || '',
            images: localProd?.images || []
          }
        }
      }));
    }
  },

  addProduct: async (product) => {
    const id = 'prod-' + Math.random().toString(36).substr(2, 9);
    const sku = product.sku || 'SZ-' + Math.floor(100000 + Math.random() * 900000);
    const { imageUrl, images, ...metadata } = product;

    const newProduct: Product = {
      ...metadata,
      id,
      sku,
      datasheetUrl: product.datasheetUrl || null as any,
      firmwareCode: product.firmwareCode || null as any,
      imageUrl: '',
      images: [],
      rating: 5.0,
      reviewsCount: 0
    };

    if (isFirebaseActive && db) {
      try {
        await dbSet(dbRef(db, `products/${id}`), removeUndefined(stripBase64Images(newProduct)));
        await dbSet(dbRef(db, `productImages/${id}`), removeUndefined({
          imageUrl: imageUrl || '',
          images: images || []
        }));

        // Populate cache immediately
        set((state) => ({
          products: [...state.products, { ...newProduct, imageUrl, images }],
          imagesMap: {
            ...state.imagesMap,
            [id]: { imageUrl: imageUrl || '', images: images || [] }
          }
        }));
      } catch (err) {
        console.error('Failed to add product to RTDB:', err);
        throw err;
      }
    } else {
      const localProduct = { ...newProduct, imageUrl, images };
      set((state) => ({
        products: [...state.products, localProduct],
        imagesMap: {
          ...state.imagesMap,
          [id]: { imageUrl: imageUrl || '', images: images || [] }
        }
      }));
    }
  },

  updateProduct: async (id, updatedFields) => {
    const current = useProductStore.getState().products.find((p) => p.id === id);
    if (!current) return;

    const { imageUrl, images, ...metadataFields } = updatedFields;
    const updatedProduct = { ...current, ...metadataFields };

    if (isFirebaseActive && db) {
      try {
        await dbSet(dbRef(db, `products/${id}`), removeUndefined(stripBase64Images(updatedProduct as any)));

        const currentImages = useProductStore.getState().imagesMap[id] || { imageUrl: '', images: [] };
        const newImages = {
          imageUrl: imageUrl !== undefined ? imageUrl : currentImages.imageUrl,
          images: images !== undefined ? images : currentImages.images
        };

        if (imageUrl !== undefined || images !== undefined) {
          await dbSet(dbRef(db, `productImages/${id}`), removeUndefined(newImages));
        }

        set((state) => ({
          products: state.products.map((p) => (p.id === id ? ({ ...updatedProduct, imageUrl: newImages.imageUrl, images: newImages.images } as Product) : p)),
          imagesMap: {
            ...state.imagesMap,
            [id]: newImages
          }
        }));
      } catch (err) {
        console.error('Failed to update product in RTDB:', err);
        throw err;
      }
    } else {
      const localUpdated = {
        ...updatedProduct,
        imageUrl: imageUrl !== undefined ? imageUrl : current.imageUrl,
        images: images !== undefined ? images : current.images
      };
      set((state) => ({
        products: state.products.map((p) => (p.id === id ? (localUpdated as Product) : p)),
        imagesMap: {
          ...state.imagesMap,
          [id]: {
            imageUrl: imageUrl !== undefined ? imageUrl : (state.imagesMap[id]?.imageUrl || ''),
            images: images !== undefined ? images : (state.imagesMap[id]?.images || [])
          }
        }
      }));
    }
  },

  deductProductStock: async (id, quantity, variantId) => {
    const current = useProductStore.getState().products.find((p) => p.id === id);
    if (!current) return;

    if (isFirebaseActive && db) {
      try {
        if (variantId && current.variants) {
          const variantIndex = current.variants.findIndex(v => v.id === variantId);
          if (variantIndex !== -1) {
            const newStock = Math.max(0, current.variants[variantIndex].stock - quantity);
            await dbSet(dbRef(db, `products/${id}/variants/${variantIndex}/stock`), newStock);

            const updatedVariants = [...current.variants];
            updatedVariants[variantIndex] = { ...updatedVariants[variantIndex], stock: newStock };
            set((state) => ({
              products: state.products.map((p) => (p.id === id ? { ...p, variants: updatedVariants } : p))
            }));
          }
        } else {
          const newStock = Math.max(0, current.stock - quantity);
          await dbSet(dbRef(db, `products/${id}/stock`), newStock);
          set((state) => ({
            products: state.products.map((p) => (p.id === id ? { ...p, stock: newStock } : p))
          }));
        }
      } catch (err) {
        console.error('Failed to deduct product stock in RTDB:', err);
      }
    } else {
      if (variantId && current.variants) {
        const updatedVariants = current.variants.map((v) =>
          v.id === variantId ? { ...v, stock: Math.max(0, v.stock - quantity) } : v
        );
        set((state) => ({
          products: state.products.map((p) => (p.id === id ? { ...p, variants: updatedVariants } : p))
        }));
      } else {
        set((state) => ({
          products: state.products.map((p) => (p.id === id ? { ...p, stock: Math.max(0, p.stock - quantity) } : p))
        }));
      }
    }
  },

  deleteProduct: async (id) => {
    if (isFirebaseActive && db) {
      try {
        await dbRemove(dbRef(db, `products/${id}`));
        await dbRemove(dbRef(db, `productImages/${id}`));
        set((state) => {
          const newMap = { ...state.imagesMap };
          delete newMap[id];
          return {
            products: state.products.filter((p) => p.id !== id),
            imagesMap: newMap
          };
        });
      } catch (err) {
        console.error('Failed to delete product from RTDB:', err);
        throw err;
      }
    } else {
      set((state) => {
        const newMap = { ...state.imagesMap };
        delete newMap[id];
        return {
          products: state.products.filter((p) => p.id !== id),
          imagesMap: newMap
        };
      });
    }
  },

  addCategory: async (category) => {
    const id = category.id || category.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const newCategory = { ...category, id, subCategories: [] };

    if (isFirebaseActive && db) {
      try {
        await dbSet(dbRef(db, `categories/${id}`), removeUndefined(newCategory));
      } catch (err) {
        console.error('Failed to add category to RTDB:', err);
        throw err;
      }
    } else {
      set((state) => ({ categories: [...state.categories, newCategory] }));
    }
  },

  addSubCategory: async (categoryId, name) => {
    const subId = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const activeCategory = useProductStore.getState().categories.find((c) => c.id === categoryId);
    if (!activeCategory) return;

    const updatedCategory = {
      ...activeCategory,
      subCategories: [...(activeCategory.subCategories || []), { id: subId, name, categoryId }]
    };

    if (isFirebaseActive && db) {
      try {
        await dbSet(dbRef(db, `categories/${categoryId}`), removeUndefined(updatedCategory));
      } catch (err) {
        console.error('Failed to add subcategory to RTDB:', err);
        throw err;
      }
    } else {
      set((state) => ({
        categories: state.categories.map((c) => (c.id === categoryId ? updatedCategory : c))
      }));
    }
  },

  deleteCategory: async (categoryId) => {
    if (isFirebaseActive && db) {
      try {
        await dbRemove(dbRef(db, `categories/${categoryId}`));

        const prodsToDelete = useProductStore.getState().products.filter((p) => p.categoryId === categoryId);
        for (const p of prodsToDelete) {
          await dbRemove(dbRef(db, `products/${p.id}`));
          await dbRemove(dbRef(db, `productImages/${p.id}`));
        }
        set((state) => {
          const newMap = { ...state.imagesMap };
          prodsToDelete.forEach(p => delete newMap[p.id]);
          return { imagesMap: newMap };
        });
      } catch (err) {
        console.error('Failed to delete category from RTDB:', err);
        throw err;
      }
    } else {
      set((state) => {
        const newMap = { ...state.imagesMap };
        const prodsToDelete = state.products.filter((p) => p.categoryId === categoryId);
        prodsToDelete.forEach(p => delete newMap[p.id]);
        return {
          categories: state.categories.filter((c) => c.id !== categoryId),
          products: state.products.filter((p) => p.categoryId !== categoryId),
          imagesMap: newMap
        };
      });
    }
  },

  deleteSubCategory: async (categoryId, subCategoryId) => {
    const activeCategory = useProductStore.getState().categories.find((c) => c.id === categoryId);
    if (!activeCategory) return;

    const updatedCategory = {
      ...activeCategory,
      subCategories: (activeCategory.subCategories || []).filter((sc) => sc.id !== subCategoryId)
    };

    if (isFirebaseActive && db) {
      try {
        await dbSet(dbRef(db, `categories/${categoryId}`), removeUndefined(updatedCategory));

        const prodsToDelete = useProductStore.getState().products.filter((p) => p.subCategoryId === subCategoryId);
        for (const p of prodsToDelete) {
          await dbRemove(dbRef(db, `products/${p.id}`));
          await dbRemove(dbRef(db, `productImages/${p.id}`));
        }
        set((state) => {
          const newMap = { ...state.imagesMap };
          prodsToDelete.forEach(p => delete newMap[p.id]);
          return { imagesMap: newMap };
        });
      } catch (err) {
        console.error('Failed to delete subcategory from RTDB:', err);
        throw err;
      }
    } else {
      set((state) => {
        const newMap = { ...state.imagesMap };
        const prodsToDelete = state.products.filter((p) => p.subCategoryId === subCategoryId);
        prodsToDelete.forEach(p => delete newMap[p.id]);
        return {
          categories: state.categories.map((c) => (c.id === categoryId ? updatedCategory : c)),
          products: state.products.filter((p) => p.subCategoryId !== subCategoryId),
          imagesMap: newMap
        };
      });
    }
  },

  resetToDefaults: async () => {
    if (isFirebaseActive && db) {
      try {
        // Clear current products & images
        const currentProducts = useProductStore.getState().products;
        for (const p of currentProducts) {
          await dbRemove(dbRef(db, `products/${p.id}`));
          await dbRemove(dbRef(db, `productImages/${p.id}`));
        }

        // Clear current categories
        const currentCategories = useProductStore.getState().categories;
        for (const c of currentCategories) {
          await dbRemove(dbRef(db, `categories/${c.id}`));
        }

        // Set default categories
        for (const c of defaultCategories) {
          await dbSet(dbRef(db, `categories/${c.id}`), removeUndefined(c));
        }

        // Set default products & images
        for (const p of defaultProducts) {
          const { imageUrl, images, ...metadata } = p;
          const cleanP = {
            ...metadata,
            imageUrl: '',
            images: []
          };
          await dbSet(dbRef(db, `products/${p.id}`), removeUndefined(stripBase64Images(cleanP)));
          await dbSet(dbRef(db, `productImages/${p.id}`), removeUndefined({
            imageUrl: imageUrl || '',
            images: images || []
          }));
        }

        set({ imagesMap: {} });
      } catch (err) {
        console.error('Failed to reset to defaults in RTDB:', err);
        throw err;
      }
    } else {
      set({
        categories: defaultCategories,
        products: defaultProducts,
        imagesMap: {}
      });
    }
  }
}));
